import { useCallback, useEffect, useRef, useState } from "react";
import { tenderService } from "../../services/api";

// D-011: the lawyer-only staff-request feed.
//
// The backend scopes GET /dashboard/tenders/ to tender_category='staff' +
// status='active' for a lawyer token, so this hook never has to guard against
// player leaks. We still send tender_category=staff redundantly — it is
// leak-safe (staff ∩ player = empty) and makes the intent explicit.
//
// "Applied" is tracked client-side: a successful apply flips the card for the
// session. The pitch list (getAgentPitches, created_by=self) has no readable
// tender reference today (PitchSerializer exposes tender_id write-only), so it
// cannot seed the applied set across reloads. seedApplied() is a defensive
// no-op that will start working automatically if the serializer ever exposes a
// tender id — see TEST-EVIDENCE "D-011 FE".
export default function useLawyerRequests() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedIds, setAppliedIds] = useState(() => new Set());

  // Bumping this re-runs the fetch effect (used by retry).
  const [reloadKey, setReloadKey] = useState(0);

  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Seed applied ids from the lawyer's own pitches. Defensive against the
  // pitch shape: today none of these keys are present, so the set stays empty
  // and we fall back to session-only applied state.
  const seedApplied = useCallback(async (signal) => {
    try {
      const params = new URLSearchParams();
      const res = await tenderService.getAgentPitches(params, signal);
      if (!res.success || !Array.isArray(res.data)) return;
      const ids = res.data
        .map((p) => p?.tender_id ?? p?.tender?.id ?? p?.tender)
        .filter((v) => v != null)
        .map((v) => String(v));
      if (ids.length) {
        setAppliedIds((prev) => new Set([...prev, ...ids]));
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      // Non-fatal: the feed still works without the applied seed.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append("view", "all");
        // Redundant but explicit + leak-safe; backend already forces staff.
        params.append("tender_category", "staff");
        if (debouncedSearch) params.append("search", debouncedSearch);

        const res = await tenderService.getAllTenders(params, controller.signal);
        if (!active) return;
        if (res.success) {
          setTenders(Array.isArray(res.data) ? res.data : []);
        } else {
          setError(res.error || "Failed to load requests");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        if (active) setError("Failed to load requests. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    seedApplied(controller.signal);

    return () => {
      active = false;
      controller.abort();
    };
  }, [debouncedSearch, reloadKey, seedApplied]);

  const markApplied = useCallback((tenderId) => {
    if (tenderId == null) return;
    setAppliedIds((prev) => new Set(prev).add(String(tenderId)));
  }, []);

  const isApplied = useCallback(
    (tenderId) => tenderId != null && appliedIds.has(String(tenderId)),
    [appliedIds]
  );

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    tenders,
    loading,
    error,
    search,
    setSearch,
    isApplied,
    markApplied,
    retry,
  };
}
